import React, { useState } from 'react';
import {
  Box, Checkbox, IconButton, MenuItem, Select, Table, TableBody, TableCell,
  TableHead, TableRow, Typography, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SendIcon from '@mui/icons-material/Send';
import CheckIcon from '@mui/icons-material/Check';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { CardWrapper, ButtonOutlined } from '../../UI';
import { DOCUMENTOS } from '../../../mocks/processos.mock';
import * as C from '../../../theme/colors.constants';

/**
 * Aba Documentos.
 *
 * Print `grid_check_docs.png`: barra com "ENVIAR" e "+ NOVO" acima do card, e a
 * tabela com paginação "Linhas por página" no rodapé.
 */
const EXT_IMAGEM = ['ico', 'png', 'jpg', 'jpeg', 'Imagem'];

export default function AbaDocumentos() {
  const [marcados, setMarcados] = useState([]);
  const [porPagina, setPorPagina] = useState(5);
  const [pagina, setPagina] = useState(0);

  const inicio = pagina * porPagina;
  const visiveis = DOCUMENTOS.slice(inicio, inicio + porPagina);
  const ultimaPagina = Math.ceil(DOCUMENTOS.length / porPagina) - 1;

  const toggle = id => setMarcados(m => (m.includes(id) ? m.filter(x => x !== id) : [...m, id]));

  return (
    <Box>
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, px: 0.5,
      }}
      >
        <ButtonOutlined
          startIcon={<SendIcon />}
          disabled={marcados.length === 0}
          sx={{ border: 'none', '&:hover': { border: 'none', backgroundColor: 'transparent' } }}
        >
          ENVIAR
          {marcados.length ? ` (${marcados.length})` : ''}
        </ButtonOutlined>
        <ButtonOutlined startIcon={<AddIcon />}>NOVO</ButtonOutlined>
      </Box>

      <CardWrapper>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={marcados.length === DOCUMENTOS.length}
                    indeterminate={marcados.length > 0 && marcados.length < DOCUMENTOS.length}
                    onChange={e => setMarcados(e.target.checked ? DOCUMENTOS.map(d => d.id) : [])}
                  />
                </TableCell>
                <TableCell>Extensão</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Descrição</TableCell>
                <TableCell>Nome do Arquivo</TableCell>
                <TableCell align="center">Original Arquivada</TableCell>
                <TableCell align="center">Oculto</TableCell>
                <TableCell align="center">Data de Arquivamento</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visiveis.map((d, i) => (
                <TableRow key={d.id} hover sx={{ backgroundColor: i % 2 === 1 ? C.paleGrey : undefined }}>
                  <TableCell padding="checkbox">
                    <Checkbox size="small" checked={marcados.includes(d.id)} onChange={() => toggle(d.id)} />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      {EXT_IMAGEM.includes(d.extensao) ? (
                        <ImageOutlinedIcon sx={{ fontSize: 17, color: C.tangerine }} />
                      ) : (
                        <InsertDriveFileOutlinedIcon sx={{ fontSize: 17, color: C.navyLight }} />
                      )}
                      {d.extensao}
                    </Box>
                  </TableCell>
                  <TableCell>{d.tipo}</TableCell>
                  <TableCell>
                    <Tooltip title={d.descricao || ''} arrow>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                      >
                        {d.descricao}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={d.nomeArquivo} arrow>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                      >
                        {d.nomeArquivo}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    {d.originalArquivada ? <CheckIcon sx={{ fontSize: 17, color: C.navy }} /> : null}
                  </TableCell>
                  <TableCell align="center">
                    {d.oculto ? <CheckIcon sx={{ fontSize: 17, color: C.navy }} /> : null}
                  </TableCell>
                  <TableCell align="center">{d.data}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2, mt: 2,
        }}
        >
          <Typography variant="body2">Linhas por página:</Typography>
          <Select
            size="small"
            value={porPagina}
            onChange={(e) => { setPorPagina(Number(e.target.value)); setPagina(0); }}
            variant="standard"
            sx={{ minWidth: 56 }}
          >
            {[5, 10, 25].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
          </Select>
          <Typography variant="body2">
            {inicio + 1}
            –
            {Math.min(inicio + porPagina, DOCUMENTOS.length)}
            {' '}
            de
            {DOCUMENTOS.length}
          </Typography>
          <IconButton size="small" disabled={pagina === 0} onClick={() => setPagina(p => p - 1)} sx={{ color: C.navy }}>
            <ChevronLeftIcon />
          </IconButton>
          <IconButton size="small" disabled={pagina >= ultimaPagina} onClick={() => setPagina(p => p + 1)} sx={{ color: C.navy }}>
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </CardWrapper>
    </Box>
  );
}
